/**
 * Fullscreen Utilities for Poker Reborn
 *
 * Shared fullscreen API with cross-browser support
 * (standard + webkit-prefixed for Safari/iOS).
 *
 * @see docs/common/FULLSCREEN_SUGGESTION.md
 */

// Webkit-extended types for Safari compatibility
type WebkitDocument = Document & {
  webkitFullscreenElement?: Element;
  webkitExitFullscreen?: () => Promise<void>;
};

type WebkitHTMLElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void>;
};

/**
 * Check if the document is currently in fullscreen mode.
 */
export function isFullscreen(): boolean {
  const doc = document as WebkitDocument;
  return !!(document.fullscreenElement || doc.webkitFullscreenElement);
}

/**
 * Enter fullscreen mode on the document element.
 */
export function enterFullscreen(): void {
  const docEl = document.documentElement as WebkitHTMLElement;

  if (docEl.webkitRequestFullscreen) {
    void docEl.webkitRequestFullscreen();
  } else {
    void docEl.requestFullscreen();
  }
}

/**
 * Exit fullscreen mode.
 */
export function exitFullscreen(): void {
  const doc = document as WebkitDocument;

  if (doc.webkitExitFullscreen) {
    void doc.webkitExitFullscreen();
  } else {
    void document.exitFullscreen();
  }
}

/**
 * Toggle fullscreen mode. Returns the new fullscreen state.
 */
export function toggleFullscreen(): boolean {
  if (isFullscreen()) {
    exitFullscreen();
    return false;
  } else {
    enterFullscreen();
    return true;
  }
}
